import React, { useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import ReactECharts from 'echarts-for-react';
import { questions } from '../data/questions';
import {
  calculateStepDropOffs,
  calculateQuestionPerformance,
  calculateCriticalDropOffs,
  calculateCategoryDropOffs,
  generateDropOffInsights
} from '../utils/analyticsCalculations';

const {
  FiRefreshCw, FiPieChart, FiBarChart2, FiTrendingUp, FiTarget, FiAlertTriangle,
  FiCheckCircle, FiAlertCircle, FiLayers, FiDownload, FiTrendingDown, FiActivity,
  FiUsers, FiEye, FiFilter, FiMonitor, FiSmartphone
} = FiIcons;

const AnalyticsTab = ({ submissions, loading }) => {
  const [view, setView] = useState('overview'); // 'overview', 'dropoffs', 'funnel', 'questions'

  // Calculate all analytics data
  const allSubmissions = submissions;
  const dropOffData = calculateStepDropOffs(allSubmissions);
  const questionPerformance = calculateQuestionPerformance(allSubmissions);
  const criticalDropOffs = calculateCriticalDropOffs(allSubmissions);
  const categoryDropOffs = calculateCategoryDropOffs(allSubmissions);
  const insights = generateDropOffInsights(allSubmissions);

  // Calculate basic metrics for overview
  const calculateCategoryAverages = () => {
    if (submissions.length === 0) return {};

    const totals = {
      "Brand Positioning": 0,
      "Website Performance": 0,
      "Marketing System": 0
    };

    const counts = {
      "Brand Positioning": 0,
      "Website Performance": 0,
      "Marketing System": 0
    };

    submissions.forEach(submission => {
      if (submission.brand_strategy_score !== undefined) {
        totals["Brand Positioning"] += submission.brand_strategy_score;
        counts["Brand Positioning"]++;
      }
      if (submission.website_ux_score !== undefined) {
        totals["Website Performance"] += submission.website_ux_score;
        counts["Website Performance"]++;
      }
      if (submission.marketing_score !== undefined) {
        totals["Marketing System"] += submission.marketing_score;
        counts["Marketing System"]++;
      }
    });

    const averages = {};
    const maxScores = {
      "Brand Positioning": 6, // 3 questions × 2 points each
      "Website Performance": 8, // 4 questions × 2 points each
      "Marketing System": 6 // 3 questions × 2 points each
    };

    for (const category in totals) {
      if (counts[category] > 0) {
        const maxTotalPoints = maxScores[category] * counts[category];
        const percentage = (totals[category] / maxTotalPoints) * 100;
        averages[category] = {
          average: (totals[category] / counts[category]).toFixed(1),
          percentage: percentage.toFixed(1),
          count: counts[category],
          maxPossible: maxScores[category]
        };
      }
    }

    return averages;
  };

  const calculateTierDistribution = () => {
    const tiers = {
      "Industry Leader": 0,
      "Optimisation Opportunities": 0,
      "Ready to Scale": 0,
      "Needs Professional Help": 0
    };

    submissions.forEach(submission => {
      if (submission.tier && tiers[submission.tier] !== undefined) {
        tiers[submission.tier]++;
      }
    });

    return tiers;
  };

  const categoryAverages = calculateCategoryAverages();
  const tierDistribution = calculateTierDistribution();

  // Chart configurations
  const funnelChartOption = {
    title: {
      text: 'Conversion Funnel',
      textStyle: { color: '#e0e0e0' }
    },
    tooltip: {
      trigger: 'item',
      formatter: '{a} <br/>{b}: {c} ({d}%)'
    },
    series: [{
      name: 'Funnel',
      type: 'funnel',
      left: '10%',
      top: 60,
      bottom: 60,
      width: '80%',
      min: 0,
      max: dropOffData.stepCounts?.landingPage || 100,
      minSize: '0%',
      maxSize: '100%',
      sort: 'descending',
      gap: 2,
      label: {
        show: true,
        position: 'inside'
      },
      labelLine: {
        length: 10,
        lineStyle: {
          width: 1,
          type: 'solid'
        }
      },
      itemStyle: {
        borderColor: '#1e1e1e',
        borderWidth: 1
      },
      emphasis: {
        label: {
          fontSize: 20
        }
      },
      data: [
        { value: dropOffData.stepCounts?.landingPage || 0, name: 'Visited Landing Page', itemStyle: { color: '#3b82f6' } },
        { value: dropOffData.stepCounts?.startedAssessment || 0, name: 'Started Assessment', itemStyle: { color: '#8b5cf6' } },
        { value: dropOffData.stepCounts?.reachedContact || 0, name: 'Reached Contact Form', itemStyle: { color: '#f59e0b' } },
        { value: dropOffData.stepCounts?.completed || 0, name: 'Completed Assessment', itemStyle: { color: '#10b981' } }
      ]
    }],
    backgroundColor: 'transparent'
  };

  const dropOffHeatmapOption = {
    title: {
      text: 'Question Drop-off Rates',
      textStyle: { color: '#e0e0e0' }
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: function(params) {
        const question = criticalDropOffs[params[0].dataIndex];
        return `${question?.questionText}<br/>Drop-off Rate: ${params[0].value}%<br/>Category: ${question?.category}`;
      }
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '15%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: criticalDropOffs.map((q, i) => `Q${q.questionNumber}`),
      axisLabel: {
        color: '#e0e0e0',
        interval: 0,
        rotate: 45
      }
    },
    yAxis: {
      type: 'value',
      max: 100,
      axisLabel: { color: '#e0e0e0' }
    },
    series: [{
      name: 'Drop-off Rate (%)',
      type: 'bar',
      data: criticalDropOffs.map(q => ({
        value: parseFloat(q.dropOffRate),
        itemStyle: {
          color: parseFloat(q.dropOffRate) > 50 ? '#ef4444' : 
                 parseFloat(q.dropOffRate) > 30 ? '#f59e0b' : '#22c55e'
        }
      }))
    }],
    backgroundColor: 'transparent'
  };

  const exportAnalytics = () => {
    let reportContent = `FITNESS BRAND HEALTH CHECK - ADVANCED ANALYTICS\n`;
    reportContent += `Generated on: ${new Date().toLocaleDateString()}\n\n`;
    
    reportContent += `CONVERSION FUNNEL ANALYSIS\n`;
    reportContent += `=========================\n`;
    reportContent += `Total Visitors: ${dropOffData.stepCounts?.landingPage || 0}\n`;
    reportContent += `Started Assessment: ${dropOffData.stepCounts?.startedAssessment || 0} (${dropOffData.funnelRates?.landingToStarted || 0}%)\n`;
    reportContent += `Reached Contact Form: ${dropOffData.stepCounts?.reachedContact || 0} (${dropOffData.funnelRates?.startedToContact || 0}%)\n`;
    reportContent += `Completed Assessment: ${dropOffData.stepCounts?.completed || 0} (${dropOffData.funnelRates?.contactToComplete || 0}%)\n`;
    reportContent += `Overall Conversion Rate: ${dropOffData.funnelRates?.overallConversion || 0}%\n\n`;

    reportContent += `CRITICAL DROP-OFF POINTS\n`;
    reportContent += `========================\n`;
    criticalDropOffs.forEach((q, index) => {
      reportContent += `${index + 1}. Question ${q.questionNumber} (${q.category})\n`;
      reportContent += `   Drop-off Rate: ${q.dropOffRate}%\n`;
      reportContent += `   Question: ${q.questionText}\n`;
      reportContent += `   Answered by: ${q.answeredCount}/${q.totalStarted} users\n\n`;
    });

    reportContent += `CATEGORY PERFORMANCE\n`;
    reportContent += `===================\n`;
    categoryDropOffs.forEach(cat => {
      reportContent += `${cat.category}: ${cat.avgDropOffRate}% avg drop-off\n`;
      reportContent += `  Worst Question: ${cat.worstQuestion?.dropOffRate}% drop-off\n\n`;
    });

    reportContent += `ACTIONABLE INSIGHTS\n`;
    reportContent += `==================\n`;
    insights.forEach((insight, index) => {
      reportContent += `${index + 1}. ${insight.title} (${insight.priority} priority)\n`;
      reportContent += `   ${insight.description}\n`;
      reportContent += `   Recommendation: ${insight.recommendation}\n\n`;
    });

    const blob = new Blob([reportContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Brand_Health_Advanced_Analytics_${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h2 className="text-2xl font-bold text-white">Advanced Analytics</h2>
        <button
          onClick={exportAnalytics}
          className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <SafeIcon icon={FiDownload} className="w-4 h-4 mr-2" />
          Export Drop-off Report
        </button>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-dark-600 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">Overall Conversion</p>
              <p className="text-2xl font-bold text-white">{dropOffData.funnelRates?.overallConversion || 0}%</p>
            </div>
            <SafeIcon icon={FiTarget} className="w-8 h-8 text-primary-500" />
          </div>
        </div>

        <div className="bg-dark-600 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">Total Visitors</p>
              <p className="text-2xl font-bold text-white">{dropOffData.stepCounts?.landingPage || 0}</p>
            </div>
            <SafeIcon icon={FiUsers} className="w-8 h-8 text-blue-500" />
          </div>
        </div>

        <div className="bg-dark-600 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">Started Assessment</p>
              <p className="text-2xl font-bold text-white">{dropOffData.stepCounts?.startedAssessment || 0}</p>
            </div>
            <SafeIcon icon={FiActivity} className="w-8 h-8 text-purple-500" />
          </div>
        </div>

        <div className="bg-dark-600 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">Completed</p>
              <p className="text-2xl font-bold text-white">{dropOffData.stepCounts?.completed || 0}</p>
            </div>
            <SafeIcon icon={FiCheckCircle} className="w-8 h-8 text-green-500" />
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        <button
          onClick={() => setView('overview')}
          className={`px-4 py-2 rounded-lg text-sm ${
            view === 'overview' ? 'bg-primary-600 text-white' : 'bg-dark-500 text-gray-300 hover:bg-dark-400'
          }`}
        >
          <SafeIcon icon={FiTrendingUp} className="w-4 h-4 inline mr-1" />
          Overview
        </button>
        <button
          onClick={() => setView('funnel')}
          className={`px-4 py-2 rounded-lg text-sm ${
            view === 'funnel' ? 'bg-primary-600 text-white' : 'bg-dark-500 text-gray-300 hover:bg-dark-400'
          }`}
        >
          <SafeIcon icon={FiFilter} className="w-4 h-4 inline mr-1" />
          Conversion Funnel
        </button>
        <button
          onClick={() => setView('dropoffs')}
          className={`px-4 py-2 rounded-lg text-sm ${
            view === 'dropoffs' ? 'bg-primary-600 text-white' : 'bg-dark-500 text-gray-300 hover:bg-dark-400'
          }`}
        >
          <SafeIcon icon={FiTrendingDown} className="w-4 h-4 inline mr-1" />
          Drop-off Analysis
        </button>
        <button
          onClick={() => setView('questions')}
          className={`px-4 py-2 rounded-lg text-sm ${
            view === 'questions' ? 'bg-primary-600 text-white' : 'bg-dark-500 text-gray-300 hover:bg-dark-400'
          }`}
        >
          <SafeIcon icon={FiEye} className="w-4 h-4 inline mr-1" />
          Question Performance
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <SafeIcon icon={FiRefreshCw} className="w-8 h-8 text-primary-600 animate-spin" />
        </div>
      ) : allSubmissions.length === 0 ? (
        <div className="text-center py-12 bg-dark-600 rounded-lg">
          <p className="text-gray-300 text-lg">No data available for analysis.</p>
        </div>
      ) : (
        <>
          {view === 'overview' && (
            <div className="space-y-6">
              {/* Critical Insights */}
              {insights.length > 0 && (
                <div className="bg-dark-600 rounded-lg p-6">
                  <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
                    <SafeIcon icon={FiAlertTriangle} className="w-5 h-5 text-danger-500 mr-2" />
                    Critical Insights
                  </h3>
                  <div className="space-y-4">
                    {insights.map((insight, index) => (
                      <div
                        key={index}
                        className={`p-4 rounded-lg border-l-4 ${
                          insight.priority === 'high' ? 'bg-danger-600 bg-opacity-10 border-danger-500' :
                          insight.priority === 'medium' ? 'bg-warning-600 bg-opacity-10 border-warning-500' :
                          'bg-blue-600 bg-opacity-10 border-blue-500'
                        }`}
                      >
                        <h4 className="font-semibold text-white mb-1">{insight.title}</h4>
                        <p className="text-gray-300 text-sm mb-2">{insight.description}</p>
                        <p className="text-gray-400 text-sm italic">{insight.recommendation}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Category Performance */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-dark-600 rounded-lg p-6">
                  <h3 className="text-xl font-semibold text-white mb-4">Category Drop-off Rates</h3>
                  <div className="space-y-4">
                    {categoryDropOffs.map((cat, index) => (
                      <div key={index} className="bg-dark-500 p-4 rounded-lg">
                        <div className="flex justify-between items-center mb-2">
                          <h4 className="font-medium text-white">{cat.category}</h4>
                          <span className={`px-2 py-1 rounded text-sm font-medium ${
                            parseFloat(cat.avgDropOffRate) > 40 ? 'bg-danger-600 text-white' :
                            parseFloat(cat.avgDropOffRate) > 25 ? 'bg-warning-600 text-white' :
                            'bg-success-600 text-white'
                          }`}>
                            {cat.avgDropOffRate}% drop-off
                          </span>
                        </div>
                        <div className="text-sm text-gray-300">
                          Worst question: {cat.worstQuestion?.dropOffRate}% drop-off
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-dark-600 rounded-lg p-6">
                  <h3 className="text-xl font-semibold text-white mb-4">Top 5 Problem Questions</h3>
                  <div className="space-y-3">
                    {criticalDropOffs.slice(0, 5).map((q, index) => (
                      <div key={index} className="bg-dark-500 p-3 rounded-lg">
                        <div className="flex justify-between items-start mb-1">
                          <span className="text-sm font-medium text-white">
                            Q{q.questionNumber}: {q.category}
                          </span>
                          <span className="text-sm font-bold text-danger-500">
                            {q.dropOffRate}%
                          </span>
                        </div>
                        <p className="text-xs text-gray-300 truncate">
                          {q.questionText}
                        </p>
                        <div className="text-xs text-gray-400 mt-1">
                          {q.answeredCount}/{q.totalStarted} users answered
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {view === 'funnel' && (
            <div className="space-y-6">
              <div className="bg-dark-600 rounded-lg p-6">
                <div className="h-[400px]">
                  <ReactECharts option={funnelChartOption} style={{ height: '100%' }} />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-dark-600 rounded-lg p-4">
                  <h4 className="font-semibold text-white mb-2">Landing → Started</h4>
                  <div className="text-2xl font-bold text-blue-500">
                    {dropOffData.funnelRates?.landingToStarted || 0}%
                  </div>
                  <p className="text-sm text-gray-400">
                    {dropOffData.stepCounts?.startedAssessment || 0} of {dropOffData.stepCounts?.landingPage || 0} visitors
                  </p>
                </div>

                <div className="bg-dark-600 rounded-lg p-4">
                  <h4 className="font-semibold text-white mb-2">Started → Contact</h4>
                  <div className="text-2xl font-bold text-warning-500">
                    {dropOffData.funnelRates?.startedToContact || 0}%
                  </div>
                  <p className="text-sm text-gray-400">
                    {dropOffData.stepCounts?.reachedContact || 0} completed questions
                  </p>
                </div>

                <div className="bg-dark-600 rounded-lg p-4">
                  <h4 className="font-semibold text-white mb-2">Contact → Complete</h4>
                  <div className="text-2xl font-bold text-success-500">
                    {dropOffData.funnelRates?.contactToComplete || 0}%
                  </div>
                  <p className="text-sm text-gray-400">
                    {dropOffData.stepCounts?.completed || 0} fully completed
                  </p>
                </div>
              </div>
            </div>
          )}

          {view === 'dropoffs' && (
            <div className="space-y-6">
              <div className="bg-dark-600 rounded-lg p-6">
                <div className="h-[400px]">
                  <ReactECharts option={dropOffHeatmapOption} style={{ height: '100%' }} />
                </div>
              </div>

              <div className="bg-dark-600 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-white mb-4">Drop-off Analysis by Category</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-dark-400">
                        <th className="text-left py-2 text-gray-300">Category</th>
                        <th className="text-left py-2 text-gray-300">Avg Drop-off</th>
                        <th className="text-left py-2 text-gray-300">Worst Question</th>
                        <th className="text-left py-2 text-gray-300">Best Question</th>
                      </tr>
                    </thead>
                    <tbody>
                      {categoryDropOffs.map((cat, index) => {
                        const bestQuestion = cat.questions.reduce((best, q) => 
                          parseFloat(q.dropOffRate) < parseFloat(best.dropOffRate) ? q : best
                        );
                        
                        return (
                          <tr key={index} className="border-b border-dark-500">
                            <td className="py-3 text-white font-medium">{cat.category}</td>
                            <td className="py-3">
                              <span className={`px-2 py-1 rounded text-xs ${
                                parseFloat(cat.avgDropOffRate) > 40 ? 'bg-danger-600' :
                                parseFloat(cat.avgDropOffRate) > 25 ? 'bg-warning-600' :
                                'bg-success-600'
                              }`}>
                                {cat.avgDropOffRate}%
                              </span>
                            </td>
                            <td className="py-3 text-gray-300">
                              Q{cat.worstQuestion?.questionNumber}: {cat.worstQuestion?.dropOffRate}%
                            </td>
                            <td className="py-3 text-gray-300">
                              Q{bestQuestion?.questionNumber}: {bestQuestion?.dropOffRate}%
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {view === 'questions' && (
            <div className="space-y-6">
              <div className="bg-dark-600 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-white mb-4">All Questions Performance</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-dark-400">
                        <th className="text-left py-2 text-gray-300">Question #</th>
                        <th className="text-left py-2 text-gray-300">Category</th>
                        <th className="text-left py-2 text-gray-300">Question Text</th>
                        <th className="text-left py-2 text-gray-300">Completion Rate</th>
                        <th className="text-left py-2 text-gray-300">Drop-off Rate</th>
                        <th className="text-left py-2 text-gray-300">Answered By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {questionPerformance.map((q, index) => (
                        <tr key={index} className="border-b border-dark-500 hover:bg-dark-500">
                          <td className="py-3 text-white font-medium">Q{q.questionNumber}</td>
                          <td className="py-3 text-gray-300">{q.category}</td>
                          <td className="py-3 text-gray-300 max-w-xs truncate">{q.questionText}</td>
                          <td className="py-3">
                            <span className={`px-2 py-1 rounded text-xs ${
                              parseFloat(q.completionRate) > 75 ? 'bg-success-600' :
                              parseFloat(q.completionRate) > 50 ? 'bg-warning-600' :
                              'bg-danger-600'
                            }`}>
                              {q.completionRate}%
                            </span>
                          </td>
                          <td className="py-3">
                            <span className={`px-2 py-1 rounded text-xs ${
                              parseFloat(q.dropOffRate) > 40 ? 'bg-danger-600' :
                              parseFloat(q.dropOffRate) > 25 ? 'bg-warning-600' :
                              'bg-success-600'
                            }`}>
                              {q.dropOffRate}%
                            </span>
                          </td>
                          <td className="py-3 text-gray-300">
                            {q.answeredCount}/{q.totalStarted}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="bg-dark-600 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-white mb-4">Optimisation Recommendations</h3>
                <div className="space-y-4">
                  <div className="bg-danger-600 bg-opacity-10 border border-danger-500 rounded-lg p-4">
                    <h4 className="font-semibold text-danger-400 mb-2">High Priority ({'>'}40% drop-off)</h4>
                    <div className="space-y-2">
                      {questionPerformance
                        .filter(q => parseFloat(q.dropOffRate) > 40)
                        .map((q, index) => (
                          <div key={index} className="text-sm text-gray-300">
                            • Question {q.questionNumber} ({q.category}): Consider simplifying or breaking into parts
                          </div>
                        ))}
                    </div>
                  </div>

                  <div className="bg-warning-600 bg-opacity-10 border border-warning-500 rounded-lg p-4">
                    <h4 className="font-semibold text-warning-400 mb-2">Medium Priority (25-40% drop-off)</h4>
                    <div className="space-y-2">
                      {questionPerformance
                        .filter(q => parseFloat(q.dropOffRate) > 25 && parseFloat(q.dropOffRate) <= 40)
                        .map((q, index) => (
                          <div key={index} className="text-sm text-gray-300">
                            • Question {q.questionNumber} ({q.category}): Review wording and add context
                          </div>
                        ))}
                    </div>
                  </div>

                  <div className="bg-success-600 bg-opacity-10 border border-success-500 rounded-lg p-4">
                    <h4 className="font-semibold text-success-400 mb-2">Performing Well ({'<'}25% drop-off)</h4>
                    <div className="text-sm text-gray-300">
                      {questionPerformance.filter(q => parseFloat(q.dropOffRate) <= 25).length} questions are performing well. 
                      Use these as templates for improving problematic questions.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AnalyticsTab;